import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, CheckIcon, TagIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORIES = [
  { value: 'real', label: 'Trabajo real' },
  { value: 'edu', label: 'Educativo' },
  { value: 'marca', label: 'Marca' },
];
const FORMATS = [
  { value: 'post', label: 'Post' },
  { value: 'reel', label: 'Reel' },
  { value: 'historia', label: 'Historia' },
  { value: 'carrusel', label: 'Carrusel' },
];

const parseTags = (s: string) => s.split(/[,\n]/).map(t => t.replace(/^#/, '').trim()).filter(Boolean);

const NewContentIdea: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('real');
  const [format, setFormat] = useState('post');
  const [notes, setNotes] = useState('');
  const [tagsText, setTagsText] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    let dead = false;
    axios.get(`/api/content-calendars/ideas/${id}`)
      .then(r => {
        if (dead) return;
        const i = r.data.idea;
        setTitle(i.title || '');
        setCategory(i.category || 'real');
        setFormat(i.format || 'post');
        setNotes(i.notes || '');
        setTagsText((i.tags || []).join(', '));
      })
      .catch(() => { toast.error('No se pudo cargar la idea'); navigate('/content-ideas'); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [id, isEdit, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Poné un título'); return; }
    setSubmitting(true);
    try {
      const body = { title: title.trim(), category, format, notes: notes.trim() || null, tags: parseTags(tagsText) };
      if (isEdit) {
        await axios.put(`/api/content-calendars/ideas/${id}`, body);
        toast.success('Idea actualizada');
      } else {
        await axios.post('/api/content-calendars/ideas', body);
        toast.success('Idea creada');
      }
      navigate('/content-ideas');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'No se pudo guardar');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/content-ideas')} className="rounded-full">
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{isEdit ? 'Editar idea' : 'Nueva idea'}</h1>
          <p className="text-muted-foreground text-sm">{isEdit ? 'Actualizá la idea de contenido' : 'Agregá una idea al banco para planificarla después'}</p>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="p-6 text-sm text-muted-foreground text-center">Cargando…</CardContent></Card>
      ) : (
        <form onSubmit={submit}>
          <Card>
            <CardHeader><CardTitle className="text-base">Datos de la idea</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="title">Título *</Label>
                <Input id="title" placeholder="Ej: Antes/después de un pulido…" value={title} onChange={e => setTitle(e.target.value)} autoFocus />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Categoría</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Formato</Label>
                  <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="notes">Bajada / nota</Label>
                <Textarea id="notes" placeholder="Idea del posteo, ángulo, qué mostrar…" value={notes} onChange={e => setNotes(e.target.value)} rows={4} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="tags" className="flex items-center gap-1"><TagIcon className="h-3.5 w-3.5" /> Etiquetas (separadas por coma)</Label>
                <Input id="tags" placeholder="taller, detailing, antes-despues…" value={tagsText} onChange={e => setTagsText(e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/content-ideas')}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>
              {isEdit ? <CheckIcon className="h-4 w-4 mr-2" /> : <PlusIcon className="h-4 w-4 mr-2" />}
              {submitting ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear idea'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default NewContentIdea;
